import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getActiveTrips, subscribeToTargetedOffersForDriver } from '../../services/tripService';
import { getFatigueStatus } from '../../services/fatigueService';
import toast from 'react-hot-toast';
import { TIER_INFO } from '../../config/vehicleTiers';

const TIER_ORDER = ['go', 'comfy', 'plus', 'vip', 'vipPlus'];

export default function DriverDashboard() {
  const { userProfile, logout } = useAuth();
  const navigate = useNavigate();
 
 const [activeTrips, setActiveTrips] = useState([]);
  const [fatigue, setFatigue] = useState(null);
  const [targetedOffers, setTargetedOffers] = useState([]);
  const [, forceTick] = useState(0);

  useEffect(() => {
    if (userProfile?.id) {
      getActiveTrips(userProfile.id, 'driver').then(setActiveTrips).catch(() => {});
      getFatigueStatus(userProfile.id).then(setFatigue).catch(() => {});
    }
  }, [userProfile]);

  // Subscribe to targeted offers (live, while driver is on dashboard)
  useEffect(() => {
    if (!userProfile?.id) return;
    const unsub = subscribeToTargetedOffersForDriver(userProfile.id, setTargetedOffers);
    return unsub;
  }, [userProfile?.id]);

  // Tick every second so the countdown updates
  useEffect(() => {
    if (targetedOffers.length === 0) return;
    const id = setInterval(() => forceTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [targetedOffers.length]);

  // Update browser tab title when offers waiting
  useEffect(() => {
    const original = 'Reservo Driver';
    if (targetedOffers.length > 0) {
      document.title = `(${targetedOffers.length}) New offer — ${original}`;
    } else {
      document.title = original;
    }
    return () => { document.title = original; };
  }, [targetedOffers.length]);


  const handleLogout = async () => {
    try { await logout(); navigate('/login'); } catch (err) { toast.error('Logout failed'); }
  };

  const cards = [
    { icon: '🚖', title: 'Available Bookings', desc: 'View pool & offers', path: '/driver/available', color: '#2dd4a8' },
    { icon: '📅', title: 'My Schedule', desc: 'View upcoming bookings', path: '/driver/schedule', color: '#2dd4a8' },
    { icon: '📋', title: 'Trip History', desc: 'View past trips', path: '/driver/trips', color: '#6366f1' },
    { icon: '✅', title: 'Pre-Trip Checklist', desc: 'Complete before driving', path: '/driver/checklist', color: '#fbbf24' },
    { icon: '📚', title: 'Training', desc: 'Complete required modules', path: '/driver/training', color: '#8b5cf6' },
    { icon: '😴', title: 'Fatigue Status', desc: 'Check driving hours', path: '/driver/fatigue', color: '#ff6b6b' },
    { icon: '🏆', title: 'My Rewards', desc: 'Points & profit share', path: '/driver/rewards', color: '#2dd4a8' },
    { icon: '📊', title: 'Tax Summaries', desc: 'BAS reports & exports', path: '/driver/tax-summary', color: '#059669' },
    { icon: '💰', title: 'My Wallet', desc: 'Balance & cashouts', path: '/driver/wallet', color: '#fbbf24' },
    { icon: '🔍', title: 'Lost Items', desc: 'Found-in-vehicle cases', path: '/driver/lost-items', color: '#f59e0b' },
    { icon: '🚗', title: 'Damage Claims', desc: 'Submit & track claims', path: '/driver/claims', color: '#dc2626' },
    { icon: '📄', title: 'My Documents', desc: 'Licence, insurance, etc.', path: '/driver/my-documents', color: '#0891b2' },
    { icon: '🔄', title: 'Annual Recheck', desc: 'Yearly compliance review', path: '/driver/my-recheck', color: '#7c3aed' },
    { icon: '📋', title: 'Compliance', desc: 'Policies & procedures', path: '/compliance', color: '#1e40af' },    
    { icon: '🎫', title: 'Support', desc: 'Get help', path: '/driver/support', color: '#94a3b8' },
    { icon: '⚙️', title: 'Settings', desc: 'Account & preferences', path: '/driver/settings', color: '#5a6578' },
  ];


  return (
    <div className="max-w-[700px] mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="font-serif mt-0 mb-1 text-brand-dark">
            Welcome, {userProfile?.firstName || 'Driver'}
          </h1>
          <div className="text-[0.9rem] text-muted">Driver ID: {userProfile?.referenceNumber || '—'}</div>
        </div>
        <button onClick={handleLogout} className="px-5 py-2.5 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold text-[0.85rem] text-body">Sign Out</button>
      </div>

      {/* Fatigue Warning */}
      {fatigue?.lockedOut && (
        <div className="p-4 rounded-2xl text-white mb-5 text-center" style={{ background: '#ff6b6b' }}>
          <div className="font-bold">🛑 Fatigue Lockout Active</div>
          <div className="text-[0.85rem] mt-1">You cannot accept bookings until limits reset.</div>
        </div>
      )}

      {/* Fatigue Summary */}
      {fatigue && !fatigue.lockedOut && (
        <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 mb-5">
          <div className="flex justify-between text-[0.85rem]">
            <span className="text-body">Today: {fatigue.dailyHours?.toFixed(1) || 0}/10 hrs</span>
            <span className="text-body">Week: {fatigue.weeklyHours?.toFixed(1) || 0}/50 hrs</span>
            <span className="text-body">Days: {fatigue.consecutiveDays || 0}/6</span>
          </div>
        </div>
      )}

      {/* Eligible service tiers */}
      {(() => {
        const serviceTier = userProfile?.serviceTier || userProfile?.vehicle?.tier || null;
        const idx = TIER_ORDER.indexOf(serviceTier);
        const eligible = idx >= 0 ? TIER_ORDER.slice(0, idx + 1) : [];
        if (!serviceTier || eligible.length === 0) return null;
        return (
          <div className="p-4 rounded-2xl bg-white border border-slate-200 mb-5">
            <div className="flex justify-between items-center mb-2">
              <div className="font-bold text-brand-dark">Your service tiers</div>
              {userProfile?.vehicle?.make && (
                <div className="text-[0.8rem] text-muted">{userProfile.vehicle.make} {userProfile.vehicle.model}</div>
              )}
            </div>
            <div className="text-[0.82rem] text-muted mb-2">You can accept bookings for these tiers:</div>
            <div className="flex flex-wrap gap-2">
              {eligible.map((t) => (
                <span key={t} className="px-2.5 py-1 rounded-full text-[0.78rem] font-bold text-white"
                  style={{ background: TIER_INFO[t]?.colour || '#5a6578' }}>
                  {TIER_INFO[t]?.name || t}{t === serviceTier ? ' ★' : ''}
                </span>
              ))}
            </div>
            <div className="text-[0.76rem] text-muted mt-2">
              ★ Your vehicle's class ({TIER_INFO[serviceTier]?.name || serviceTier}). Higher tiers require a qualifying vehicle.
            </div>
          </div>
        );
      })()}

      {/* Targeted Offer Banner */}
      {targetedOffers.length > 0 && (() => {
        const next = targetedOffers[0];
        const expiresAt = next.offerExpiresAt?.toDate ? next.offerExpiresAt.toDate() : (next.offerExpiresAt ? new Date(next.offerExpiresAt) : null);
        const secondsLeft = expiresAt ? Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)) : null;
        const formatCountdown = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
        return (
          <div onClick={() => navigate('/driver/available')} className="p-[18px] rounded-2xl mb-4 cursor-pointer text-white"
            style={{
              background: 'linear-gradient(135deg, #a855f7, #d8b4fe)',
              animation: secondsLeft != null && secondsLeft < 60 ? 'pulse 1s infinite' : 'none',
            }}>
            <div className="flex justify-between items-center">
              <div>
                <div className="font-extrabold text-[1.05rem]">
                  📩 {targetedOffers.length === 1 ? 'New booking offer' : `${targetedOffers.length} booking offers`}
                </div>
                <div className="text-[0.85rem] opacity-90 mt-0.5">
                  {next.referenceNumber} — {next.pickupAddress?.split(',')[0] || 'Pickup'} → {next.destinationAddress?.split(',')[0] || 'Destination'}
                </div>
                {secondsLeft != null && (
                  <div className="text-[0.85rem] font-bold mt-1">
                    ⏱ Respond within {formatCountdown(secondsLeft)}
                  </div>
                )}
              </div>
              <span className="text-[1.5rem]">→</span>
            </div>
          </div>
        );
      })()}

      {/* Active Trip Banner */}
      {activeTrips.length > 0 && (
        <div onClick={() => navigate(`/driver/trip/${activeTrips[0].id}`)} className="p-5 rounded-2xl mb-6 cursor-pointer"
          style={{ background: 'linear-gradient(135deg, #2dd4a8, #6ee7b7)', color: '#0a1628' }}>
          <div className="flex justify-between items-center">
            <div>
              <div className="font-extrabold text-[1.1rem]">Active Trip</div>
              <div className="text-[0.9rem] opacity-80">
                {activeTrips[0].referenceNumber} — {activeTrips[0].pickupAddress || 'Pickup'}
              </div>
            </div>
            <span className="text-[1.5rem]">→</span>
          </div>
        </div>
      )}

      {/* Action Cards */}
      <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        {cards.map((card) => (
          <div key={card.title} onClick={() => navigate(card.path)} className="p-6 rounded-[20px] border border-slate-200 bg-white cursor-pointer text-center transition-all"
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div className="text-[2rem] mb-3">{card.icon}</div>
            <div className="font-bold text-brand-dark mb-1">{card.title}</div>
            <div className="text-[0.8rem] text-muted">{card.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}